import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schema?: object | object[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://floridahousesforsale.deals';

export default function SEO({ title, description, keywords, canonical, ogImage, schema }: SEOProps) {
  useEffect(() => {
    // Title
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    // Core meta
    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('robots', 'index, follow');
    setMeta('author', 'Amparo Jarquin');

    // Last modified
    setMeta('last-modified', new Date().toISOString().split('T')[0]);

    // Canonical
    const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
    setLink('canonical', canonicalUrl);

    // Open Graph
    setMeta('og:type', 'website', true);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:site_name', 'Florida Homes for Sale', true);
    if (ogImage) setMeta('og:image', ogImage, true);

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    // Geo tags
    setMeta('geo.region', 'US-FL');
    setMeta('geo.placename', 'West Palm Beach, Florida');
    setMeta('geo.position', '26.7153;-80.0534');
    setMeta('ICBM', '26.7153, -80.0534');

    // JSON-LD Schema
    const existingSchemas = document.querySelectorAll('script[data-seo-schema]');
    existingSchemas.forEach((s) => s.remove());

    const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];
    schemas.forEach((s) => {
      const scriptEl = document.createElement('script');
      scriptEl.setAttribute('type', 'application/ld+json');
      scriptEl.setAttribute('data-seo-schema', 'true');
      scriptEl.textContent = JSON.stringify(s);
      document.head.appendChild(scriptEl);
    });

    return () => {
      // cleanup schemas on unmount
      document.querySelectorAll('script[data-seo-schema]').forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonical, ogImage, schema]);

  return null;
}
